import { Inject, Injectable, PLATFORM_ID, TransferState, makeStateKey } from '@angular/core';
import { LayoutServiceData } from '@sitecore-jss/sitecore-jss-angular';
import { map, shareReplay, catchError } from 'rxjs/operators';
import { Observable, of as observableOf, BehaviorSubject } from 'rxjs';
import { JssState } from './JssState';
import { JssLayoutService, LayoutServiceError } from './layout/jss-layout.service';
import { ActivatedRoute } from '@angular/router';
import { JssDataFetcherService } from './jss-data-fetcher.service';
import { environment } from '../environments/environment';

export const jssKey = makeStateKey<JssState>('jss');

/**
 * Stores the JSS app's context (current route and Sitecore context data).
 * This implementation runs on the client (browser) side; see jss-context.server-side.service.ts
 * for the implementation that runs on the server (SSR) side.
 */
@Injectable()
export class JssContextService {
  // components can subscribe to this (or use getValue()) to get access to latest data from Layout Service,
  // as well as current language and server route
  state: BehaviorSubject<JssState>;
  public $customParams: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private _customParams: any[] = [];

  constructor(protected transferState: TransferState, protected layoutService: JssLayoutService,protected dataFetcher: JssDataFetcherService,
    protected activatedRoute: ActivatedRoute,
    @Inject(PLATFORM_ID) protected readonly platformId: string) {
    this.state = new BehaviorSubject<JssState>(new JssState());
    this.$customParams.subscribe((params) => {
      this._customParams = params;
    });
  }

  changeLanguage(language: string) {
    this.state.next({ ...this.state.value, language });
  }

  // primarily invoked by JssRouteResolver on URL/route change
  changeRoute(route: string, language: string): Observable<JssState> {
    // on client initial load, retrieve initial state from server
    const foundInitialState = this.transferState.hasKey(jssKey);
    if (foundInitialState) {
      const jssState = this.transferState.get<JssState>(jssKey, null);
      this.transferState.remove(jssKey);
      this.state.next(jssState);
      return observableOf(jssState);
    }

    //const appLanguage = this.state.value.language || language;
    const appLanguage = language;

    const fetchOptions : any = {
      layoutServiceConfig: { host: environment.sitecoreApiHost },
      querystringParams: {
        sc_lang: language,
        sc_apikey: environment.sitecoreApiKey
      },
      fetcher: this.dataFetcher.fetch,
    };

    if (this._customParams && this._customParams.length) {
      fetchOptions.querystringParams['sc_params'] = this.encodeCustomParams(this._customParams);
    }

    const jssState$ = this.layoutService.getRouteData(route, appLanguage).pipe(
      map((routeData) => {
        const lsResult = routeData as LayoutServiceData;

        const result = new JssState();
        result.sitecore = lsResult.sitecore ? lsResult.sitecore : null;
        result.language = appLanguage;
        result.serverRoute = route;
        return result;
      }),
      catchError((error: LayoutServiceError) => {
        const result = new JssState();
        result.language = appLanguage;
        result.serverRoute = route;
        result.routeFetchError = error;
        return observableOf(result);
      }),
      shareReplay(1)
    );

    // subscribe to it ourselves so we can maintain current state
    jssState$.subscribe((jssState) => {
      this.state.next(jssState);
    });

    return jssState$;
  }

  public encodeCustomParams(params?: any[]): string {
    if (!params) {
      params = this._customParams;
    }

    if (typeof btoa === 'undefined') {
      //In Node js does not exist btoa
      return Buffer.from(unescape(encodeURIComponent(JSON.stringify(params)))).toString('base64');
    }
    return btoa(unescape(encodeURIComponent(JSON.stringify(params))));
  }

  public customParamsToQueryStringObj(params?: any[]): any {

    if (!params) {
      params = this._customParams;
    }
    return { 'sc_params': this.encodeCustomParams(params) };
  }

  public decodeCustomParams(params: string): any[] {
    try {
      if (typeof atob === 'undefined') {
        //In Node js does not exist atob
        return JSON.parse(decodeURIComponent(unescape(Buffer.from(params, 'base64').toString())));
      }
      return JSON.parse(decodeURIComponent(escape(atob(params))));
    } catch (err) {
      return null;
    }
  }

}
